import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sun, Waves, Thermometer, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const SnowbirdPage = () => {
  const benefits = [
    "Escape the cold in sunny Florida",
    "3-minute walk to Atlantic beaches",
    "Fully furnished—just bring your bags",
    "Full kitchen for home cooking",
    "Private hot tub for relaxation",
    "Quiet, upscale neighborhood",
    "Golf, dining, and shopping nearby",
    "Seasonal rates available",
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Snowbird Rentals Indialantic FL | Winter Beach House 3–9 Months | Paradise Beach Rental"
        description="Escape winter at Paradise Beach Rental — a furnished snowbird rental 3 minutes from the beach in Indialantic, Florida. Seasonal stays from 90 days up to 9 months. Pet-friendly."
        path="/snowbird-rentals"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/75r1b3hg_IMG_6451.jpeg"
              alt="Snowbird Rental"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0A1628]/60" />
          </div>
          <div className="relative z-10 container-premium pb-20 pt-40">
            <div className="gold-line mb-6" />
            <span className="label-sm text-[#C9A962]">Seasonal Rentals</span>
            <h1 className="headline-xl text-white mt-4 mb-6 max-w-3xl">
              Snowbird Rentals on Florida's Space Coast
            </h1>
            <p className="body-lg text-white/80 max-w-2xl mb-8">
              Escape winter in a luxury beachside residence. January through April availability 
              for seasonal guests seeking sun, sand, and comfort.
            </p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-6 text-base">
                Check Seasonal Availability
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Benefits */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/onn5i5f9_IMG_7098.jpeg"
                  alt="Hot Tub"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="headline-lg text-[#0A1628] mb-6">Your Winter Paradise</h2>
                <p className="body-md text-[#0A1628]/60 mb-8">
                  Leave the snow behind and settle into your private Florida beach home. 
                  Everything you need for a comfortable seasonal stay, steps from the ocean.
                </p>
                <div className="space-y-3">
                  {benefits.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <Check className="w-4 h-4 text-[#C9A962]" />
                      <span className="text-sm text-[#0A1628]/70">{b}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-[#C9A962]">
          <div className="container-premium">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <Sun className="w-8 h-8 text-[#0A1628] mx-auto mb-2" />
                <p className="font-display text-3xl text-[#0A1628] mb-1">237</p>
                <p className="text-sm text-[#0A1628]/70">Sunny Days/Year</p>
              </div>
              <div>
                <Waves className="w-8 h-8 text-[#0A1628] mx-auto mb-2" />
                <p className="font-display text-3xl text-[#0A1628] mb-1">3 min</p>
                <p className="text-sm text-[#0A1628]/70">Walk to Beach</p>
              </div>
              <div>
                <Thermometer className="w-8 h-8 text-[#0A1628] mx-auto mb-2" />
                <p className="font-display text-3xl text-[#0A1628] mb-1">72°F</p>
                <p className="text-sm text-[#0A1628]/70">Avg Winter Temp</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-lg bg-[#0A1628]">
          <div className="container-premium text-center">
            <h3 className="headline-md text-white mb-4">Peak Season Fills Fast</h3>
            <p className="body-md text-white/60 mb-8 max-w-xl mx-auto">
              January through April is our busiest season. Contact us early to secure your dates.
            </p>
            <Link to="/contact">
              <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-4">
                Request Seasonal Quote
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default SnowbirdPage;
